import { BiddingBidMarket } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { JepxSettlement } from '@ebalance/proto/bffuser/v1/jepx_settlement_model_pb'

import { isValidCivilDate } from '~/models/civilDate'
import { addDays, formatDateValue, fromProtoDate } from '~/models/dateTime'
import type {
  SettlementDetailLanding,
  SettlementDetailMarket,
} from '~/models/settlementDetailLanding'

/**
 * 特定の約定にひも付かない清算書の件名に含まれる語 (間接送電権・定額制の手数料)。
 * 判定は JEPX の原文の件名に対して行う (英訳した表示では判定しない)。
 */
const NO_DETAIL_SUBJECT_KEYWORDS = ['間接送電権', '定額制'] as const

/** 清算書の行に「詳細」を出すか (AC-JST-010)。 */
export function hasSettlementDetail(title: string): boolean {
  return !NO_DETAIL_SUBJECT_KEYWORDS.some((keyword) => title.includes(keyword))
}

/**
 * 清算日から、 開く日までの日数。 eSLJ の「詳細」と同じ日: スポットは清算日の翌日 (受渡日)、
 * 時間前は清算日の前日 (約定日)。 時間前は、 eSLJ が約定日で絞るのに対し、 eBalance の約定履歴は
 * 受渡日で絞るので、 開いた日と清算書の取引の範囲は一致しない (#11048)。
 */
const DAYS_FROM_SETTLEMENT_DATE: Record<SettlementDetailMarket, number> = {
  spot: 1,
  intraday: -1,
}

const DETAIL_MARKET: Partial<Record<BiddingBidMarket, SettlementDetailMarket>> =
  {
    [BiddingBidMarket.SPOT]: 'spot',
    [BiddingBidMarket.INTRADAY]: 'intraday',
  }

/**
 * 「詳細」で開く先 (市場と日)。 「詳細」を出さない行と、 清算日が暦に無い行は undefined (AC-JST-009・010)。
 */
export function toSettlementDetailLanding(
  row: JepxSettlement,
): SettlementDetailLanding | undefined {
  const market = DETAIL_MARKET[row.market]
  if (market === undefined || !hasSettlementDetail(row.title)) return undefined
  const { settlementDate } = row
  if (settlementDate === undefined || !isValidCivilDate(settlementDate)) {
    return undefined
  }
  const date = addDays(
    fromProtoDate(settlementDate),
    DAYS_FROM_SETTLEMENT_DATE[market],
  )
  return { market, date: formatDateValue(date) }
}
